/**
 * Les langues dans lesquelles un collaborateur peut S'ENTRAÎNER.
 *
 * ── À ne pas confondre avec les langues de l'INTERFACE ───────────────────────
 * `SUPPORTED_LANGUAGES` (src/i18n/pick-language.ts) liste les langues dans lesquelles
 * BizCoach est TRADUIT : "fr" et "en", et cette liste n'a pas vocation à grossir — chaque
 * ajout supposerait de traduire 3 772 clés et d'y veiller à chaque évolution.
 *
 * La liste ci-dessous est d'une autre nature : ce sont les langues dans lesquelles l'IA peut
 * CONVERSER et RÉDIGER. Rien à traduire côté produit, le modèle s'en charge. C'est pourquoi
 * elle peut être large là où l'autre reste à deux.
 *
 * Les deux listes doivent rester séparées. Les fusionner reviendrait à promettre une interface
 * en trente langues.
 *
 * ── Le nom affiché est dans la langue elle-même ──────────────────────────────
 * « Deutsch », pas « Allemand ». C'est un manager francophone qui choisit pour un collaborateur
 * germanophone : le nom natif est ce que ce collaborateur reconnaîtra, et il lève l'ambiguïté
 * des langues proches (Nederlands / Deutsch, Português / Español).
 */

export interface SpokenLanguage {
  /** Code ISO 639-1, celui envoyé à N8N sous la clé `spokenLanguage`. */
  code: string;
  /** Nom dans la langue elle-même — ce que lit l'humain qui remplit le formulaire. */
  nativeName: string;
  /**
   * Nom ANGLAIS, envoyé à N8N à côté du code sous la clé `spokenLanguageName`.
   *
   * Un code ISO seul est ambigu pour un modèle de langue : notre catalogue contient 53
   * paires de codes séparées par UNE lettre, dont `hu`/`hy` (hongrois/arménien),
   * `hy`/`he` (arménien/hébreu) et `he`/`hi` (hébreu/hindi). Un modèle qui confond deux
   * de ces codes au milieu d'un long prompt rédige le débrief dans la mauvaise langue,
   * sans lever la moindre erreur. Le nom en toutes lettres retire cette ambiguïté.
   */
  englishName: string;
}

/**
 * Trente-trois langues : les plus répandues dans le monde des affaires, celles où un modèle de
 * langue actuel est réellement à l'aise. Ajouter une langue ici n'exige aucune autre
 * modification du produit — c'est le seul endroit à toucher.
 */
export const SPOKEN_LANGUAGES: readonly SpokenLanguage[] = [
  { code: "fr", nativeName: "Français", englishName: "French" },
  { code: "en", nativeName: "English", englishName: "English" },
  { code: "es", nativeName: "Español", englishName: "Spanish" },
  { code: "de", nativeName: "Deutsch", englishName: "German" },
  { code: "it", nativeName: "Italiano", englishName: "Italian" },
  { code: "pt", nativeName: "Português", englishName: "Portuguese" },
  { code: "nl", nativeName: "Nederlands", englishName: "Dutch" },
  { code: "pl", nativeName: "Polski", englishName: "Polish" },
  { code: "ro", nativeName: "Română", englishName: "Romanian" },
  { code: "sv", nativeName: "Svenska", englishName: "Swedish" },
  { code: "da", nativeName: "Dansk", englishName: "Danish" },
  { code: "fi", nativeName: "Suomi", englishName: "Finnish" },
  { code: "no", nativeName: "Norsk", englishName: "Norwegian" },
  { code: "cs", nativeName: "Čeština", englishName: "Czech" },
  { code: "sk", nativeName: "Slovenčina", englishName: "Slovak" },
  { code: "hu", nativeName: "Magyar", englishName: "Hungarian" },
  { code: "el", nativeName: "Ελληνικά", englishName: "Greek" },
  { code: "bg", nativeName: "Български", englishName: "Bulgarian" },
  { code: "hr", nativeName: "Hrvatski", englishName: "Croatian" },
  { code: "sl", nativeName: "Slovenščina", englishName: "Slovenian" },
  { code: "et", nativeName: "Eesti", englishName: "Estonian" },
  { code: "lv", nativeName: "Latviešu", englishName: "Latvian" },
  { code: "lt", nativeName: "Lietuvių", englishName: "Lithuanian" },
  { code: "tr", nativeName: "Türkçe", englishName: "Turkish" },
  { code: "ru", nativeName: "Русский", englishName: "Russian" },
  { code: "uk", nativeName: "Українська", englishName: "Ukrainian" },
  { code: "hy", nativeName: "Հայերեն", englishName: "Armenian" },
  { code: "ar", nativeName: "العربية", englishName: "Arabic" },
  { code: "he", nativeName: "עברית", englishName: "Hebrew" },
  { code: "zh", nativeName: "中文", englishName: "Chinese" },
  { code: "ja", nativeName: "日本語", englishName: "Japanese" },
  { code: "ko", nativeName: "한국어", englishName: "Korean" },
  { code: "hi", nativeName: "हिन्दी", englishName: "Hindi" },
] as const;

const CODES = new Set(SPOKEN_LANGUAGES.map((l) => l.code));

/** Le code est-il une langue d'entraînement connue ? */
export function isSpokenLanguage(code: unknown): code is string {
  return typeof code === "string" && CODES.has(code);
}

/**
 * Valide une langue d'entraînement reçue d'un formulaire.
 *
 * Renvoie `null` pour « non renseignée » (le collaborateur suivra sa langue d'interface), et
 * `undefined` pour « valeur refusée » — l'appelant doit alors répondre 400.
 *
 * Volontairement STRICT, contrairement au patron de `users.language`
 * (`body.language === "en" ? "en" : "fr"`), qui corrige en silence. Une langue d'entraînement mal
 * saisie ne provoquerait aucune erreur visible : la simulation se déroulerait simplement dans
 * la mauvaise langue, et personne ne s'en apercevrait avant un client.
 */
export function parseSpokenLanguage(raw: unknown): string | null | undefined {
  if (raw === null || raw === undefined || raw === "") return null;
  return isSpokenLanguage(raw) ? raw : undefined;
}

/** Le nom natif d'une langue (pour l'écran), ou le code si elle est inconnue. */
export function spokenLanguageNativeName(code: string | null | undefined): string {
  if (!code) return "";
  return SPOKEN_LANGUAGES.find((l) => l.code === code)?.nativeName ?? code;
}

/**
 * Le nom ANGLAIS d'une langue — à envoyer à N8N à côté du code.
 *
 * Repli sur le code pour une valeur aberrante en base : mieux vaut transmettre « xx » que
 * rien du tout, le code reste la clé et N8N peut le signaler.
 */
export function spokenLanguageEnglishName(code: string | null | undefined): string {
  if (!code) return "";
  return SPOKEN_LANGUAGES.find((l) => l.code === code)?.englishName ?? code;
}

/**
 * LA langue dans laquelle l'IA doit s'exprimer pour cet utilisateur.
 *
 * Point de passage UNIQUE : c'est ici que vit le repli sur la langue d'interface. Recopier
 * `user.spokenLanguage ?? user.language` ailleurs finirait par diverger — c'est exactement ce
 * qui est arrivé à `canMngPilotChallenge` (deux handlers sur quatre avaient oublié la règle)
 * et au contrôle des packs one-shot dans `licence-check`.
 *
 * Envoyée à N8N sous la clé `spokenLanguage`, jamais `spokenLanguage` : une seule lettre la
 * sépare alors de `userLanguage`, qui reste la langue de l'interface et continue de partir
 * telle quelle. Deux clés voisines dans un payload, c'est un débrief dans la mauvaise langue
 * que rien ne signale.
 */
export function resolveSpokenLanguage(
  user: { spokenLanguage?: string | null; language?: string | null } | null | undefined,
): string {
  if (!user) return "fr";
  const usage = user.spokenLanguage;
  if (usage && isSpokenLanguage(usage)) return usage;
  return user.language === "en" ? "en" : "fr";
}
